import * as React from 'react';
import * as styles from './InvidooButton.module.scss';

export type InvidooButtonVariant = 'primary' | 'secondary' | 'text';
export type InvidooButtonSize = 'small' | 'medium' | 'large';

export interface InvidooButtonProps {
  /** Button style variant */
  variant?: InvidooButtonVariant;
  /** Button size */
  size?: InvidooButtonSize;
  /** Click handler */
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  /** Additional CSS classes */
  className?: string;
  /** Inline styles */
  style?: React.CSSProperties;
  /** Disabled state */
  disabled?: boolean;
  /** HTML button type attribute */
  type?: 'button' | 'submit' | 'reset';
  /** Icon rendered close to the label on the left */
  iconStart?: React.ReactNode;
  /** Icon rendered close to the label on the right */
  iconEnd?: React.ReactNode;
  /** Icon pinned to the button's left edge (e.g., back chevron) */
  navIconStart?: React.ReactNode;
  /** Icon pinned to the button's right edge (e.g., next chevron) */
  navIconEnd?: React.ReactNode;
  /** Button label */
  children: React.ReactNode;
  /** Full width button (width: 100%) */
  fullWidth?: boolean;
}

/**
 * InvidooButton — standardized button component with proper icon alignment.
 *
 * Supports four icon positions:
 * - iconStart/iconEnd: icons close to the label text
 * - navIconStart/navIconEnd: icons pinned to the button edges (for prev/next navigation)
 *
 * The label is always wrapped in a span for consistent flex alignment.
 *
 * Outputs:
 * - Scoped CSS module classes for styling
 * - `.ivd-btn` global class for parent-scoped overrides
 * - `data-variant` and `data-size` attributes for targeting by variant/size
 */
const InvidooButton: React.FC<InvidooButtonProps> = ({
  variant = 'primary',
  size = 'large',
  onClick,
  className,
  style,
  disabled = false,
  type = 'button',
  iconStart,
  iconEnd,
  navIconStart,
  navIconEnd,
  children,
  fullWidth = false,
}) => {
  // Map variant prop to CSS module class
  const variantClassMap: Record<InvidooButtonVariant, string> = {
    primary: styles.variantPrimary,
    secondary: styles.variantSecondary,
    text: styles.variantText,
  };

  // Map size prop to CSS module class (large is the default, no extra class)
  const sizeClassMap: Record<InvidooButtonSize, string> = {
    large: '',
    medium: styles.sizeMedium,
    small: styles.sizeSmall,
  };

  const hasAnyIcon = iconStart || iconEnd || navIconStart || navIconEnd;

  const buttonClasses = [
    // Scoped styles from CSS module
    styles.root,
    variantClassMap[variant],
    sizeClassMap[size],
    fullWidth ? styles.fullWidth : '',
    hasAnyIcon ? styles.hasIcon : '',
    iconStart ? styles.hasIconStart : '',
    iconEnd ? styles.hasIconEnd : '',
    navIconStart ? styles.hasNavIconStart : '',
    navIconEnd ? styles.hasNavIconEnd : '',
    // Stable utility class for parent-scoped overrides (replaces legacy .invidoo-button)
    'ivd-btn',
    // Custom className from props
    className || '',
  ].filter(Boolean).join(' ');

  return (
    <button
      type={type}
      className={buttonClasses}
      onClick={onClick}
      disabled={disabled}
      style={style}
      data-variant={variant}
      data-size={size}
    >
      {/* Nav icon at left edge */}
      {navIconStart && (
        <span className={styles.navIconStart}>{navIconStart}</span>
      )}

      {/* Icon close to label on left */}
      {iconStart && (
        <span className={styles.iconStart}>{iconStart}</span>
      )}

      {/* Label always wrapped for proper flex alignment */}
      <span className={styles.label}>{children}</span>

      {/* Icon close to label on right */}
      {iconEnd && (
        <span className={styles.iconEnd}>{iconEnd}</span>
      )}

      {/* Nav icon at right edge */}
      {navIconEnd && (
        <span className={styles.navIconEnd}>{navIconEnd}</span>
      )}
    </button>
  );
};

export default InvidooButton;
